import {t} from 'i18next';
import {ReportDataListItem} from '../../../../../api/types/reportingApi.type';
import {buildTreeItems} from '../../../../../utils/reportUtils';
import {
  AccordionSection,
  FilterComponent,
  Section,
  Reports,
  ColumnConfig,
} from '../../config/reportTypes';
import dayjs from 'dayjs';

export const ReviewerTimeOnlineReportType = 'RTO006';

type DateHeader = {
  date: string;
  isweekend: number;
};

type TreeItem = {
  id: string;
  fileType: string;
  children: TreeItem[];
};

class TimeSpan {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;

  private constructor(
    days: number,
    hours: number,
    minutes: number,
    seconds: number
  ) {
    this.days = days;
    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;
  }

  static fromSeconds(totalSeconds: number): TimeSpan {
    const days = Math.floor(totalSeconds / (24 * 3600));
    totalSeconds %= 24 * 3600;

    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return new TimeSpan(days, hours, minutes, seconds);
  }
}

export const convertTimeStampToHHMMSS = (tTime: TimeSpan): string => {
  let hr: string;

  if (tTime.days > 0) {
    hr = (tTime.hours + tTime.days * 24).toString();
  } else {
    hr = tTime.hours >= 10 ? tTime.hours.toString() : '0' + tTime.hours;
  }

  const min =
    tTime.minutes >= 10 ? tTime.minutes.toString() : '0' + tTime.minutes;
  const sec =
    tTime.seconds >= 10 ? tTime.seconds.toString() : '0' + tTime.seconds;

  const time = hr + ':' + min + ':' + sec;
  return time;
};

export const getReviewerTimeOnlieColumns = (): ColumnConfig[] => {
  return [
    {
      field: 'reviewer',
      headerName: t('reviewerTimeOnline.reviewer'),
      flex: 157 / 1048,
      minWidth: 157,
      isVisible: true,
    },
  ];
};

export const getAssignmetColumn = (): ColumnConfig[] => {
  return [
    {
      field: 'assignmentName',
      headerName: t('reviewerTimeOnline.assignmentName'),
      flex: 157 / 1048,
      minWidth: 157,
      isVisible: true,
    },
    {
      field: 'assignmentPath',
      headerName: t('reviewerTimeOnline.assignmentPath'),
      flex: 157 / 1048,
      minWidth: 157,
      isVisible: true,
    },
  ];
};

export const createDynamicColumns = (
  dateHeaderList: DateHeader[],
  filterValues: Record<string, any>
) => {
  const columns = dateHeaderList.map((dateHeader, index) => {
    const dateObj = new Date(dateHeader.date);
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const formattedDate = `${year}/${month}/${day}`;

    return {
      field: `date_${index}`,
      headerName: formattedDate,
      flex: 157 / 1048,
      minWidth: 157,
      isVisible: true,
    };
  });

  return filterValues?.selectedAssignments?.length > 0
    ? [...getAssignmetColumn(), ...columns]
    : [...columns];
};

export const getTimeOnlieColumn = (): ColumnConfig[] => {
  return [
    {
      field: 'timeOnline',
      headerName: t('reviewerTimeOnline.timeOnline'),
      flex: 157 / 1048,
      minWidth: 157,
      isVisible: true,
    },
  ];
};

export const createGridRows = (
  reportDataList: ReportDataListItem[],
  totalTimeOfDay: number[],
  totalTime: number,
  dateCount: number
) => {
  if (reportDataList.length === 0) return [];

  const rows = reportDataList.map((item, index: number) => {
    const row: any = {
      id: `${item.reviewerName}_${index}`,
      reviewer: item.reviewerName || '',
      assignmentName: item.assignmentName || '',
      assignmentPath: item.assignmentPath || '',
      timeOnline: convertTimeStampToHHMMSS(
        TimeSpan.fromSeconds(item.reviewerTime)
      ),
    };

    for (let i = 0; i < dateCount; i++) {
      row[`date_${i}`] = convertTimeStampToHHMMSS(
        // eslint-disable-next-line security/detect-object-injection
        TimeSpan.fromSeconds(item.timebydate[i])
      );
    }

    return row;
  });

  const totalRow: any = {
    id: t('reviewerTimeOnline.total'),
    reviewer: t('reviewerTimeOnline.total'),
    timeOnline: convertTimeStampToHHMMSS(TimeSpan.fromSeconds(totalTime)),
  };
  for (let i = 0; i < dateCount; i++) {
    totalRow[`date_${i}`] = convertTimeStampToHHMMSS(
      // eslint-disable-next-line security/detect-object-injection
      TimeSpan.fromSeconds(totalTimeOfDay[i])
    );
  }

  return [...rows, totalRow];
};

const getFolderIds = (arr: TreeItem[]): string[] => {
  let folderIds: string[] = [];

  arr.forEach((item) => {
    if (item.fileType === 'folder') {
      folderIds.push(item.id);
      if (item.children && item.children.length > 0) {
        folderIds = folderIds.concat(getFolderIds(item.children));
      }
    }
  });

  return folderIds;
};

export const mergeApiDataIntoConfig = (
  report: Reports,
  apiData: any,
  savedReportData?: any,
  initialSelectedFilter?: any
) => {
  const config = report.config;
  const configObject = {
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (
              comp.input_type === 'checkboxList' &&
              filter.id === 'reviewers'
            ) {
              const transformedData = apiData.reviewerList.map(
                (reviewer: any, index: number) => ({
                  label: reviewer.reviewer_Name,
                  // eslint-disable-next-line security/detect-object-injection
                  value: savedReportData?.selectedReviewers?.[index] ?? false,
                })
              );
              return {
                ...comp,
                childLabels: transformedData,
              };
            } else if (
              comp.input_type === 'multiSelectTreeViewList' &&
              filter.id === 'assignmentGroup'
            ) {
              const transformedData = buildTreeItems(
                apiData.lstAssignmentGroup
              );
              return {
                ...comp,
                treeData: transformedData,
                defaultExpandedIds: getFolderIds(transformedData),
                initialValues: (savedReportData?.selectedAssignments ?? []).map(
                  (id: any) => id.toString()
                ),
              };
            } else if (
              comp.input_type === 'datePicker' &&
              filter.id === 'dateRangeSection'
            ) {
              let initialValue = '';
              const now = dayjs().utc();
              const defaultFrom = now
                .subtract(1, 'week')
                .format('YYYY/MM/DD HH:mm:ss');
              const defaultTo = now.format('YYYY/MM/DD HH:mm:ss');
              if (initialSelectedFilter) {
                initialValue = initialSelectedFilter[comp.key]?.split(' ')?.[0];
              } else {
                initialValue =
                  comp.key === 'fromDateTime' ? defaultFrom : defaultTo;
              }

              if (savedReportData?.[comp.key]) {
                initialValue = savedReportData[comp.key]?.split(' ')?.[0];
              }

              const initialDate = initialValue
                ? dayjs(initialValue).format('YYYY/MM/DD HH:mm:ss')
                : undefined;
              return {
                ...comp,
                initialValues: initialDate,
                initialStartDate: initialDate,
                maxDateTime: dayjs(),
              };
            } else if (
              comp.input_type === 'textField' &&
              filter.id === 'dateRangeSection'
            ) {
              return {
                ...comp,
                overrideArrProps: {
                  ...comp.overrideArrProps,
                  defaultValue: savedReportData?.timeOffset,
                },
              };
            }
            return comp;
          }),
        };
      }),
    })),
  };
  return {
    ...report,
    config: {
      selectionCriteria: config.selectionCriteria,
      sections: configObject.sections,
    },
  };
};

export const getSelectedReviewer = (
  filterValues: Record<string, any>,
  savedReportData?: any
) => {
  const selectedValues = filterValues?.selectedReviewers;
  const reviewersList = savedReportData?.reviewerList;

  const selectedReviewerIDs = selectedValues
    ?.map((isSelected: boolean, index: number) =>
      // eslint-disable-next-line security/detect-object-injection
      isSelected ? reviewersList[index]?.reviewerID : null
    )
    .filter((id: any): id is number => id !== null);

  return selectedReviewerIDs;
};
